import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { 
  FileText, 
  Building2, 
  Users, 
  TrendingUp, 
  Clock, 
  CheckCircle2, 
  Plus,
  ArrowRight,
  Mail
} from 'lucide-react';
import { getReportes, getStartups, getSocios } from '../services/apiService';

const Dashboard = ({ onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    reportes: {
      total: 0,
      completados: 0,
      enProceso: 0,
      ultimoTrimestre: null
    },
    startups: {
      total: 0,
      completas: 0,
      pendientes: 0
    },
    socios: {
      total: 0,
      inversionTotal: 0
    }
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Cargar reportes
        const reportes = await getReportes();
        
        // Cargar startups
        const startups = await getStartups();
        
        // Cargar socios
        const socios = await getSocios();
        
        // Calcular estadísticas de reportes
        const reportesStats = {
          total: reportes.length || 0,
          completados: reportes.filter(r => r.estado === 'completado').length || 0,
          enProceso: reportes.filter(r => r.estado === 'en-proceso').length || 0,
          ultimoTrimestre: reportes.length > 0 ? reportes[0] : null
        };
        
        // Calcular estadísticas de startups
        const startupsStats = {
          total: startups.length || 0,
          completas: 0,
          pendientes: 0
        };
        
        // Calcular estadísticas de socios
        const inversionTotal = socios.reduce((sum, socio) => {
          return sum + (parseFloat(socio.inversion) || 0);
        }, 0);
        
        const sociosStats = {
          total: socios.length || 0,
          inversionTotal: inversionTotal
        };
        
        setStats({
          reportes: reportesStats,
          startups: startupsStats,
          socios: sociosStats
        });
      } catch (error) {
        console.error('Error cargando datos del dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Resumen general de la actividad del club de inversión
        </p>
      </div>

      {/* Estadísticas principales */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Reportes */}
        <Card className="border-border/50 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Reportes</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.reportes.total}</div>
            <p className="text-xs text-muted-foreground">
              {stats.reportes.completados} completados, {stats.reportes.enProceso} en proceso
            </p>
          </CardContent>
        </Card>

        {/* Startups Participadas */}
        <Card className="border-border/50 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Startups Participadas</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.startups.total}</div>
            <p className="text-xs text-muted-foreground">
              Startups en cartera
            </p>
          </CardContent>
        </Card>

        {/* Socios */}
        <Card className="border-border/50 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Socios</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.socios.total}</div>
            <p className="text-xs text-muted-foreground">
              Miembros del club
            </p>
          </CardContent>
        </Card>

        {/* Inversión Total */}
        <Card className="border-border/50 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inversión Total</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.socios.inversionTotal)}</div>
            <p className="text-xs text-muted-foreground">
              Capital comprometido
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Accesos rápidos y detalles */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Reportes - Detalle */}
        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Reportes Trimestrales
            </CardTitle>
            <CardDescription>
              Gestiona los reportes trimestrales del club
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total</span>
                <span className="font-medium">{stats.reportes.total}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-green-600" />
                  Completados
                </span>
                <span className="font-medium text-green-600">{stats.reportes.completados}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3 text-yellow-600" />
                  En proceso
                </span>
                <span className="font-medium text-yellow-600">{stats.reportes.enProceso}</span>
              </div>
            </div>
            <div className="pt-4 border-t border-border/50">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => onNavigate && onNavigate('preparacion-reportes')}
              >
                Ver Reportes
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Startups - Detalle */}
        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Startups Participadas
            </CardTitle>
            <CardDescription>
              Portafolio de inversión del club
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total en cartera</span>
                <span className="font-medium">{stats.startups.total}</span>
              </div>
            </div>
            <div className="pt-4 border-t border-border/50 space-y-2">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => onNavigate && onNavigate('gestion-participadas')}
              >
                Gestionar Startups
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Socios - Detalle */}
        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-5 w-5" />
              Socios del Club
            </CardTitle>
            <CardDescription>
              Miembros y sus inversiones
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total de socios</span>
                <span className="font-medium">{stats.socios.total}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Inversión total</span>
                <span className="font-medium">{formatCurrency(stats.socios.inversionTotal)}</span>
              </div>
            </div>
            <div className="pt-4 border-t border-border/50">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => onNavigate && onNavigate('listado-socios')}
              >
                Ver Socios
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Accesos rápidos */}
      <Card className="border-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Accesos Rápidos</CardTitle>
          <CardDescription>
            Accede rápidamente a las funciones más utilizadas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={() => onNavigate && onNavigate('preparacion-reportes')}
            >
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  <span className="font-medium">Nuevo Reporte</span>
                </div>
                <span className="text-xs text-muted-foreground">Crear reporte trimestral</span>
              </div>
            </Button>

            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={() => onNavigate && onNavigate('gestion-participadas')}
            >
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  <span className="font-medium">Nueva Startup</span>
                </div>
                <span className="text-xs text-muted-foreground">Agregar startup</span>
              </div>
            </Button>

            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={() => onNavigate && onNavigate('listado-socios')}
            >
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span className="font-medium">Nuevo Socio</span>
                </div>
                <span className="text-xs text-muted-foreground">Registrar socio</span>
              </div>
            </Button>

            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={() => onNavigate && onNavigate('configuracion')}
            >
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span className="font-medium">Plantillas Email</span>
                </div>
                <span className="text-xs text-muted-foreground">Configurar emails</span>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;

